// Pure helpers for the section-editor title split. A tech-spec section is stored as one
// `## {title}\n\n{body}` markdown blob; the editor splits it into a required Title field + a body
// editor so the heading can never be deleted (a headingless section silently merges into its
// neighbour on the next reload). Storage is unchanged — these functions reassemble the same blob.
//
// The materiality contract these keep:
//   - unedited section  -> reassembled markdown is BYTE-IDENTICAL to the original (no spurious
//     save / "Product updated"), even for a docling-escaped heading like `## include\_raster`;
//   - body-only edit    -> the heading LINE is preserved verbatim (no heading byte-drift);
//   - typed/edited title -> re-escaped so it survives `headingOf`'s unescape and renders literally.

/** Auto-name applied by the safety net when a section is left with content but no title. */
export const AUTO_TITLE = 'Untitled section';

// The markdown special chars docling backslash-escapes in a heading — the EXACT set headingOf's
// unescape reverses, so escapeHeading is its true inverse (unescape(escapeHeading(s)) === s). A
// literal backslash is deliberately NOT in the set: unescape only strips a backslash that precedes
// one of these chars, so escaping a lone backslash would never be reversed and would compound on
// every title edit (N\A -> N\\A -> N\\\\A). `{`/`}` are absent for the same reason — the frontend
// unescape doesn't cover them, so escaping them would leave a visible backslash and break symmetry.
// (A lone backslash before a non-special char already renders literally in markdown — no escape
// needed.)
const HEADING_SPECIALS = /[_*`~[\]()#+.!-]/g;

/** Strip a backslash-escape before a markdown special char (`include\_raster` -> `include_raster`).
 *  Mirrors the un-escape docling / the card title use; the canonical inverse of escapeHeading. */
export const unescape = (s: string): string => s.replace(/\\([_*`~[\]()#+.!-])/g, '$1');

/** Re-escape a title's markdown special chars so it round-trips through `headingOf` (which
 *  un-escapes on load) and renders as literal text, not markdown. Inverse of `unescape`. */
export const escapeHeading = (s: string): string => s.replace(HEADING_SPECIALS, '\\$&');

/** Body to seed the editor with: everything AFTER the heading line — empty for a title-only
 *  section (so the heading is never re-shown / re-editable), or the whole blob for headingless
 *  front matter. Unlike `bodyOf`, this returns '' for `## Title` (a bug where that returns the
 *  heading itself), which matters now that "empty body + title" is a valid, creatable state. */
export function sectionBody(md: string): string {
  const nl = md.indexOf('\n');
  const first = (nl === -1 ? md : md.slice(0, nl)).trim();
  const hasHeading = /^#{1,2}\s+/.test(first);
  if (hasHeading) return nl === -1 ? '' : md.slice(nl + 1).trim();
  return md.trim();
}

function firstLine(md: string): string {
  const nl = md.indexOf('\n');
  return (nl === -1 ? md : md.slice(0, nl)).trim();
}

/** Reassemble a section's markdown from the (possibly edited) title + body of one edit session.
 *
 *  Materiality-safe by construction:
 *   - nothing changed        -> return the ORIGINAL bytes verbatim (the true no-op, independent of
 *                               escape symmetry — so `onCommit`'s `prev.md === md` guard fires);
 *   - body changed only      -> keep the original heading LINE verbatim (no heading byte-drift);
 *   - title changed          -> serialize `## {escapeHeading(title)}` for the new title.
 *  An empty body yields `## {title}` (valid — no dangling blank line). */
export function combineSection(p: {
  originalMd: string;
  initialTitle: string;
  initialBody: string;
  title: string;
  body: string;
}): string {
  const titleDirty = p.title !== p.initialTitle;
  const bodyDirty = p.body !== p.initialBody;
  if (!titleDirty && !bodyDirty) return p.originalMd;
  const body = p.body.trim();
  const heading = titleDirty ? `## ${escapeHeading(p.title.trim())}` : firstLine(p.originalMd);
  return body ? `${heading}\n\n${body}` : heading;
}

export type Finalize = { kind: 'commit'; title: string } | { kind: 'discard' };

/** Decide what happens when a section-editor session ends (Done / focus-leave / unmount):
 *   - a title present       -> commit it (trimmed);
 *   - empty title, no body, and it was a fresh add -> discard the abandoned card;
 *   - empty title otherwise -> auto-name "Untitled section" (never lose content, never save a
 *     title-less/merging section). */
export function resolveFinalize(p: {
  title: string;
  bodyEmpty: boolean;
  isFreshAdd: boolean;
}): Finalize {
  const t = p.title.trim();
  if (t) return { kind: 'commit', title: t };
  if (p.bodyEmpty && p.isFreshAdd) return { kind: 'discard' };
  return { kind: 'commit', title: AUTO_TITLE };
}
